import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Moon, ArrowRight, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchNakshatra } from "@/lib/api";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ShareNakshatra() {
  const { slug, pada } = useParams();
  const [nakshatra, setNakshatra] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchNakshatra(slug);
        setNakshatra(data);
      } catch {
        // not found
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
      </div>
    );
  }

  if (!nakshatra) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-4">
        <p className="text-white/40">Nakshatra not found.</p>
        <Link to="/"><Button variant="ghost" className="text-white/60">Go Home</Button></Link>
      </div>
    );
  }

  const padaNum = parseInt(pada) || 1;
  const padaContent = nakshatra[`pada_${padaNum}`] || "";
  const imageUrl = nakshatra.featured_image_url
    ? (nakshatra.featured_image_url.startsWith("http") ? nakshatra.featured_image_url : nakshatra.featured_image_url)
    : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="share-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="share-nav-logo">
            <img src="/logo.png" alt="Daily Nakshatra" className="h-8 w-auto" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/60 hover:text-white hover:bg-white/5 text-xs tracking-wider uppercase"
            onClick={() => navigate("/calculator")}
            data-testid="share-calc-btn"
          >
            Find Yours
          </Button>
        </div>
      </nav>

      <main className="pt-14 flex items-center justify-center min-h-[calc(100vh-56px)] px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-xl py-12"
        >
          {/* Share Card */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.08)" }}
            data-testid="share-card"
          >
            {/* Image Header */}
            {imageUrl && (
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={imageUrl} alt={nakshatra.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              </div>
            )}

            {/* Content */}
            <div className="p-6 sm:p-8" style={{ background: "#0A0A0A" }}>
              <p className="text-white/30 text-xs tracking-[0.2em] uppercase mb-3">Natal Moon Placement</p>

              <h1 className="text-4xl sm:text-5xl font-light text-white/95 mb-2" data-testid="share-nakshatra-name">
                {nakshatra.name}
              </h1>
              <p className="text-white/60 text-lg mb-2" data-testid="share-pada">
                Pada {padaNum}
              </p>

              <div className="flex flex-wrap gap-x-5 gap-y-1 mb-6 text-sm">
                <span className="text-white/40">Ruler: <span className="text-white/60">{nakshatra.planetary_ruler}</span></span>
                <span className="text-white/40">Deity: <span className="text-white/60">{nakshatra.deity?.split("(")[0]?.trim()}</span></span>
                <span className="text-white/40">Symbol: <span className="text-white/60">{nakshatra.symbolism}</span></span>
              </div>

              {/* Birth Traits */}
              {nakshatra.birth_traits && (
                <div className="mb-6 pt-5 border-t border-white/5" data-testid="share-birth-traits">
                  <div className="flex items-center gap-2 mb-3">
                    <Fingerprint className="w-4 h-4 text-white/40" />
                    <p className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Traits</p>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed">
                    {nakshatra.birth_traits.replace(/\*\*/g, "")}
                  </p>
                </div>
              )}

              {/* Pada Insight */}
              {padaContent && (
                <div className="mb-6 pt-5 border-t border-white/5" data-testid="share-pada-content">
                  <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-3">Pada {padaNum} Insight</p>
                  <p className="text-white/55 text-sm leading-relaxed">
                    {padaContent.replace(/\*\*(.*?)\*\*/g, "$1")}
                  </p>
                </div>
              )}

              {/* CTA */}
              <div className="pt-5 border-t border-white/5 flex flex-col sm:flex-row gap-3">
                <Button
                  className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-11 text-sm tracking-wider flex-1"
                  onClick={() => navigate("/calculator")}
                  data-testid="share-discover-btn"
                >
                  Discover Your Nakshatra
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  className="text-white/50 hover:text-white hover:bg-white/5 rounded-full px-6 h-11 text-sm"
                  onClick={() => navigate(`/nakshatras/${slug}`)}
                  data-testid="share-deep-dive-btn"
                >
                  Deep Dive
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
